import type { DeviceResponseV5, DeviceServiceEvent } from '@emma/api_client/types';
import { Box, Text } from '@lego/klik-ui';
import { deploymentIssuesTableColumns } from '../common/table/column_definitions/DeploymentIssues';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';

type Props = {
  device: DeviceResponseV5;
  fleetId: number;
};

const deploymentIssuesDescription =
  'Application events related to this deployment. These events can provide insights into whether Docker Compose is running or not';

export const DeviceDeploymentIssues = ({ device, fleetId }: Props) => {
  const deployment = device.deployments?.find((d) => d.fleetId === fleetId && d.isActive);

  if (!deployment?.serviceEvents) {
    return null;
  }

  const componentEvents = buildServiceEvents(deployment!.serviceEvents);

  return (
    <Box background="gray.50" borderRadius="md" padding={4} width="full">
      {componentEvents.length === 0 ? (
        <Text color="gray.600" variant="p">
          No events found
        </Text>
      ) : (
        <TableStoreProvider name="DeploymentEvents">
          <Table
            columns={deploymentIssuesTableColumns}
            count={componentEvents.length || 0}
            data={componentEvents}
            description={deploymentIssuesDescription}
            hideControls={true}
            nestedStyling={true}
            title="Detected events for reported deployment"
          />
        </TableStoreProvider>
      )}
    </Box>
  );
};

const buildServiceEvents = (serviceEvents: {
  services: Record<string, { version: string; events: DeviceServiceEvent[] }>;
}): DeviceServiceEvent[] => {
  const allEvents: DeviceServiceEvent[] = [];

  // Flatten all events from all services
  Object.values(serviceEvents.services).forEach((service) => {
    allEvents.push(...service.events);
  });

  // Sort by timestamp descending (newest first)
  return allEvents.sort((a, b) => {
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
};
